import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RequestStatus } from '../../../../shared/enums/request-status';
import { ResponseErrorWithHandled } from '../../../../shared/types';
import {
  getAccountSubscriptionRequest,
  getLeadFinderSubscriptionRequest,
  getConnectedUsersAndEmailAccountsRequest,
  getInvoiceURLRequest,
  getPlansRequest,
  getLeadFinderPlansRequest,
  purchaseSubscriptionRequest,
  purchaseLeadFinderSubscriptionRequest,
  applyCouponCodeRequest,
  getEmailVerificationCreditsRequest,
  getEmailVerificationPlanRequest,
  purchaseEmailVerificationCreditsRequest,
  modifySubscriptionRequest,
  purchaseModifySubscriptionRequest,
  purchaseLeadFinderModifySubscriptionRequest,
  cancelLeadFinderAddonRequest,
  reactivateSubscriptionRequest,
  reactivateLeadFinderSubscriptionRequest,
  resumeSubscriptionRequest,
  resumeLeadFinderSubscriptionRequest,
  handleSubscriptionRequest,
  handleLeadFinderChurnkeySubscriptionRequest,
  calculatePayRequest,
  upgradePlanRequest,
  downgradePlanRequest,
  purchaseLtdPlanRequest,
  upgradeLtdPlanRequest,
  modifyLtdPlanRequest,
  getAccountSubscriptionPlanRequest,
  getPurchaseHistoryRequest,
  getPurchaseDetailsByIdRequest,
  exportCreditHistoryRequest,
  getLeadFinderOneTimePlansRequest,
  purchaseLeadFinderOneTimePlanRequest,
  dynamicCustomPlansRequest,
  getInboxAnalyzerSubscriptionRequest,
  getInboxAnalyzerPlansRequest,
  purchaseInboxAnalyzerSubscriptionRequest,
  purchaseInboxAnalyzerModifySubscriptionRequest,
  handleInboxAnalyzerChurnkeySubscriptionRequest,
  modifyInboxAnalyzerSubscriptionRequest,
  calculateInboxAnalyzerPayRequest,
  getInboxAnalyzerSubscriptionPlanRequest,
  reactivateInboxAnalyzerSubscriptionRequest,
  resumeInboxAnalyzerSubscriptionRequest,
  getInboxAnalyzerInvoiceURLRequest,
  calculateAiCreditsPricingsRequest,
  purchaseAiCreditsRequest,
  getDefaultAiCreditRateRequest,
  aiCreditHistoryRequest,
  schedulePlanUpgradeRequest,
  calculateCreditsPricesRequest,
  purchaseCreditsRequest,
  getCreditsTransactionStatusRequest,
  getPaddleSubscriptionMetadataRequest,
  pollPaddleTransactionStatusRequest,
  createPaddleCustomerPortalSessionRequest,
  revokePaddleScheduledPauseRequest,
  revokePauseSubscriptionRequest,
} from './extra-actions';
import { renewDomainsRequest } from '../../../email-accounts/extra-actions';
import {
  ApplyCouponCodeResponse,
  GetAccountSubscriptionResponse,
  GetAccountSubscriptionPlanResponse,
  GetConnectedUsersAndEmailAccountsResponse,
  GetEmailVerificationCreditsResponse,
  GetEmailVerificationPlanResponse,
  GetInvoiceURLResponse,
  GetPlansResponse,
  PurchaseModifySubscriptionResponse,
  PurchaseEmailVerificationCreditsResponse,
  ModifySubscriptionResponse,
  ReactivateResumeSubscriptionResponse,
  HandleSubscriptionResponse,
  SelectedPlanDetails,
  CalculatePayResponse,
} from './types';
import { PurchaseDetails, PurchaseHistory } from './types/purchase-history';
import { GetLeadFinderPlanResponse } from './types/response/get-lead-finder-credit-plans';
import { GetLeadFinderPlansStructuredResponse } from './types/response/get-lead-finder-plans';
import { RenewDomainsResponse } from '../../../email-accounts/types/request-payload';
import { PurchaseLeadFinderCreditsResponse } from './types/purchase-lead-finder-credits';
import {
  CalculateAiCreditsPricingsResponse,
  GetDefaultAiCreditRateResponse,
  PurchaseAiCreditsResponse,
} from './types/ai-credits';
import {
  CalculateCreditsPricesResponse,
  CreditsTransactionStatusResponse,
  PurchaseCreditsResponse,
} from './types/purchase-credits';
import type {
  PaddleSubscriptionMetadataItem,
  PaddlePollingState,
  PaddleApplicationType,
} from './types/paddle';

type RequestState = {
  status: RequestStatus;
  message: string;
  error: ResponseErrorWithHandled;
};

type State = {
  getAccountSubscriptionRequest: RequestState;
  getAccountSubscriptionPlanRequest: RequestState;
  getInboxAnalyzerSubscriptionPlanRequest: RequestState;
  getLeadFinderSubscriptionRequest: RequestState;
  getInboxAnalyzerSubscriptionRequest: RequestState;
  getPlansRequest: RequestState;
  getDynamicCustomPlansRequest: RequestState;
  getLeadFinderPlansRequest: RequestState;
  getInboxAnalyzerPlansRequest: RequestState;
  getInvoiceURLRequest: RequestState;
  getInboxAnalyzerInvoiceURLRequest: RequestState;
  getConnectedUsersAndEmailAccountsRequest: RequestState;
  applyCouponCodeRequest: RequestState;
  purchaseSubscriptionRequest: RequestState;
  purchaseLeadFinderSubscriptionRequest: RequestState;
  purchaseInboxAnalyzerSubscriptionRequest: RequestState;
  getEmailVerificationCreditsRequest: RequestState;
  getEmailVerificationPlanRequest: RequestState;
  purchaseEmailVerificationCreditsRequest: RequestState;
  modifySubscriptionRequest: RequestState;
  modifyInboxAnalyzerSubscriptionRequest: RequestState;
  purchaseModifySubscriptionRequest: RequestState;
  purchaseLeadFinderModifySubscriptionRequest: RequestState;
  cancelLeadFinderAddonRequest: RequestState;
  purchaseInboxAnalyzerModifySubscriptionRequest: RequestState;
  reactivateSubscriptionRequest: RequestState;
  reactivateLeadFinderSubscriptionRequest: RequestState;
  reactivateInboxAnalyzerSubscriptionRequest: RequestState;
  resumeSubscriptionRequest: RequestState;
  resumeLeadFinderSubscriptionRequest: RequestState;
  resumeInboxAnalyzerSubscriptionRequest: RequestState;
  revokePauseSubscriptionRequest: RequestState;
  calculateAiCreditsPricingsRequest: RequestState;
  purchaseAiCreditsRequest: RequestState;
  calculateCreditsPricesRequest: RequestState;
  purchaseCreditsRequest: RequestState;
  getDefaultAiCreditRateRequest: RequestState;
  getCreditsTransactionStatusRequest: RequestState;

  getAccountSubscriptionResponse: GetAccountSubscriptionResponse;
  getAccountSubscriptionPlanResponse: GetAccountSubscriptionPlanResponse;
  getLeadFinderSubscriptionResponse: GetAccountSubscriptionResponse;
  getInboxAnalyzerSubscriptionResponse: GetAccountSubscriptionResponse;
  getInboxAnalyzerSubscriptionPlanResponse: GetAccountSubscriptionPlanResponse;
  getPlansResponse: GetPlansResponse;
  getDynamicCustomPlansResponse: GetPlansResponse;
  getLeadFinderPlansResponse: GetLeadFinderPlansStructuredResponse;
  getInboxAnalyzerPlansResponse: GetPlansResponse;
  getInvoiceResponse: GetInvoiceURLResponse;
  getInboxAnalyzerInvoiceResponse: GetInvoiceURLResponse;
  getConnectedUsersAndEmailAccountsResponse: GetConnectedUsersAndEmailAccountsResponse;
  applyCouponCodeResponse: ApplyCouponCodeResponse;
  getEmailVerificationCreditsResponse: GetEmailVerificationCreditsResponse;
  getEmailVerificationPlanResponse: GetEmailVerificationPlanResponse[];
  cancelEmailVerificationPlanRequest: RequestState;
  purchaseEmailVerificationCreditsResponse: PurchaseEmailVerificationCreditsResponse;
  modifySubscriptionResponse: ModifySubscriptionResponse;
  modifyInboxAnalyzerSubscriptionResponse: ModifySubscriptionResponse;
  purchaseModifySubscriptionResponse: PurchaseModifySubscriptionResponse;
  purchaseLeadFinderModifySubscriptionResponse: PurchaseModifySubscriptionResponse;
  purchaseInboxAnalyzerModifySubscriptionResponse: PurchaseModifySubscriptionResponse;
  reactivateSubscriptionResponse: ReactivateResumeSubscriptionResponse;
  reactivateLeadFinderSubscriptionResponse: ReactivateResumeSubscriptionResponse;
  reactivateInboxAnalyzerSubscriptionResponse: ReactivateResumeSubscriptionResponse;
  resumeSubscriptionResponse: ReactivateResumeSubscriptionResponse;
  resumeLeadFinderSubscriptionResponse: ReactivateResumeSubscriptionResponse;
  resumeInboxAnalyzerSubscriptionResponse: ReactivateResumeSubscriptionResponse;
  handleSubscriptionRequest: RequestState;
  handleLeadFinderChurnkeySubscriptionRequest: RequestState;
  handleInboxAnalyzerChurnkeySubscriptionRequest: RequestState;
  handleSubscriptionResponse: HandleSubscriptionResponse;
  handleLeadFinderChurnkeySubscriptionResponse: HandleSubscriptionResponse;
  handleInboxAnalyzerChurnkeySubscriptionResponse: HandleSubscriptionResponse;
  selectedPlanDetails: SelectedPlanDetails;
  calculatePayRequest: RequestState;
  calculateInboxAnalyzerPayRequest: RequestState;
  upgradePlanRequest: RequestState;
  downgradePlanRequest: RequestState;
  purchaseLtdPlanRequest: RequestState;
  upgradeLtdPlanRequest: RequestState;
  modifyLtdPlanRequest: RequestState;
  getCalculatePayResponse: CalculatePayResponse;
  getCalculateInboxAnalyzerPayResponse: CalculatePayResponse;
  purchaseLtdPlanResponse: ReactivateResumeSubscriptionResponse;
  upgradeLtdPlanResponse: ReactivateResumeSubscriptionResponse;
  modifyLtdPlanResponse: ReactivateResumeSubscriptionResponse;
  purchaseHistory: PurchaseHistory[];
  purchaseDetailsById: PurchaseDetails;
  getPurchaseHistoryRequest: RequestState;
  getPurchaseDetailsByIdRequest: RequestState;
  renewDomainsRequest: RequestState;
  renewDomainsResponse: RenewDomainsResponse | null;
  exportCreditHistoryRequest: RequestState;
  aiCreditHistoryRequest: RequestState;
  getLeadFinderOneTimePlansRequest: RequestState;
  getLeadFinderOneTimePlansResponse: GetLeadFinderPlanResponse[];
  purchaseLeadFinderOneTimePlanRequest: RequestState;
  purchaseLeadFinderOneTimePlanResponse: PurchaseLeadFinderCreditsResponse;
  purchaseAiCreditsResponse: PurchaseAiCreditsResponse;
  calculateAiCreditsPricingsResponse: CalculateAiCreditsPricingsResponse;
  defaultAiCreditRateResponse: GetDefaultAiCreditRateResponse;
  schedulePlanUpgradeRequest: RequestState;
  calculateCreditsPricesResponse: CalculateCreditsPricesResponse;
  purchaseCreditsResponse: PurchaseCreditsResponse;
  getCreditsTransactionStatusResponse: CreditsTransactionStatusResponse;

  // ── Paddle state fields ────────────────────────────────────────────────────
  paddleSubscriptionMetadata: PaddleSubscriptionMetadataItem[];

  // Paddle RequestStatus entries
  getPaddleSubscriptionMetadataStatus: RequestStatus;
  pollPaddleTransactionStatusStatus: RequestStatus;
  createPaddleCustomerPortalSessionStatus: RequestStatus;
  revokePaddleScheduledPauseStatus: RequestStatus;

  paddlePolling: PaddlePollingState;
};

const initialState: State = {
  // ** API Requests States **
  getAccountSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getAccountSubscriptionPlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getInboxAnalyzerSubscriptionPlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getLeadFinderSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getInboxAnalyzerSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getInvoiceURLRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getInboxAnalyzerInvoiceURLRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPlansRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getDynamicCustomPlansRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getLeadFinderPlansRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getInboxAnalyzerPlansRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseLeadFinderSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseInboxAnalyzerSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getConnectedUsersAndEmailAccountsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  applyCouponCodeRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailVerificationCreditsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailVerificationPlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseEmailVerificationCreditsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  cancelEmailVerificationPlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  modifySubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  modifyInboxAnalyzerSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseModifySubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseLeadFinderModifySubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  cancelLeadFinderAddonRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseInboxAnalyzerModifySubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  reactivateSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  reactivateLeadFinderSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  reactivateInboxAnalyzerSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  resumeSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  resumeLeadFinderSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  resumeInboxAnalyzerSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  revokePauseSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  handleSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  handleLeadFinderChurnkeySubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  handleInboxAnalyzerChurnkeySubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  calculatePayRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  calculateInboxAnalyzerPayRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  upgradePlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  downgradePlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseLtdPlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  upgradeLtdPlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  modifyLtdPlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  calculateAiCreditsPricingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseAiCreditsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  calculateCreditsPricesRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseCreditsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getDefaultAiCreditRateRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getCreditsTransactionStatusRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  // ** Credits states **
  selectedPlanDetails: null,
  getAccountSubscriptionResponse: null,
  getAccountSubscriptionPlanResponse: null,
  getLeadFinderSubscriptionResponse: null,
  getInboxAnalyzerSubscriptionResponse: null,
  getInboxAnalyzerSubscriptionPlanResponse: null,
  getInvoiceResponse: null,
  getInboxAnalyzerInvoiceResponse: null,
  getPlansResponse: null,
  getDynamicCustomPlansResponse: null,
  getLeadFinderPlansResponse: { basePlans: [], addonPlans: [], comboPlans: [] },
  getInboxAnalyzerPlansResponse: null,
  getConnectedUsersAndEmailAccountsResponse: null,
  applyCouponCodeResponse: null,
  getEmailVerificationCreditsResponse: null,
  getEmailVerificationPlanResponse: null,
  purchaseEmailVerificationCreditsResponse: null,
  modifySubscriptionResponse: null,
  modifyInboxAnalyzerSubscriptionResponse: null,
  getCalculatePayResponse: null,
  getCalculateInboxAnalyzerPayResponse: null,
  purchaseModifySubscriptionResponse: null,
  purchaseLeadFinderModifySubscriptionResponse: null,
  purchaseInboxAnalyzerModifySubscriptionResponse: null,
  reactivateSubscriptionResponse: null,
  reactivateLeadFinderSubscriptionResponse: null,
  reactivateInboxAnalyzerSubscriptionResponse: null,
  resumeSubscriptionResponse: null,
  resumeLeadFinderSubscriptionResponse: null,
  resumeInboxAnalyzerSubscriptionResponse: null,
  handleSubscriptionResponse: null,
  handleLeadFinderChurnkeySubscriptionResponse: null,
  handleInboxAnalyzerChurnkeySubscriptionResponse: null,
  purchaseLtdPlanResponse: null,
  upgradeLtdPlanResponse: null,
  modifyLtdPlanResponse: null,
  purchaseHistory: [],
  purchaseDetailsById: null,
  getPurchaseHistoryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPurchaseDetailsByIdRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  renewDomainsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  renewDomainsResponse: null,
  exportCreditHistoryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  aiCreditHistoryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getLeadFinderOneTimePlansRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getLeadFinderOneTimePlansResponse: null,
  purchaseLeadFinderOneTimePlanRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  purchaseLeadFinderOneTimePlanResponse: null,

  purchaseAiCreditsResponse: null,

  calculateAiCreditsPricingsResponse: null,
  defaultAiCreditRateResponse: null,
  schedulePlanUpgradeRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  calculateCreditsPricesResponse: null,
  purchaseCreditsResponse: null,
  getCreditsTransactionStatusResponse: null,

  paddleSubscriptionMetadata: [],

  getPaddleSubscriptionMetadataStatus: RequestStatus.Ideal,
  pollPaddleTransactionStatusStatus: RequestStatus.Ideal,
  createPaddleCustomerPortalSessionStatus: RequestStatus.Ideal,
  revokePaddleScheduledPauseStatus: RequestStatus.Ideal,

  paddlePolling: {
    isActive: false,
    applicationType: null,
    pollingCount: 0,
    status: 'idle',
  },
};

const billingSubscriptionSlice = createSlice({
  name: 'billingSubscription',
  initialState,
  reducers: {
    resetSubscription: (state) => {
      state.getAccountSubscriptionRequest =
        initialState.getAccountSubscriptionRequest;
      state.getAccountSubscriptionPlanRequest =
        initialState.getAccountSubscriptionPlanRequest;
      state.getInboxAnalyzerSubscriptionPlanRequest =
        initialState.getInboxAnalyzerSubscriptionPlanRequest;
      state.getLeadFinderSubscriptionRequest =
        initialState.getLeadFinderSubscriptionRequest;
      state.getInboxAnalyzerSubscriptionRequest =
        initialState.getInboxAnalyzerSubscriptionRequest;
      state.getAccountSubscriptionResponse =
        initialState.getAccountSubscriptionResponse;
      state.getAccountSubscriptionPlanResponse =
        initialState.getAccountSubscriptionPlanResponse;
      state.getLeadFinderSubscriptionResponse =
        initialState.getLeadFinderSubscriptionResponse;
      state.getInboxAnalyzerSubscriptionResponse =
        initialState.getInboxAnalyzerSubscriptionResponse;
      state.getInboxAnalyzerSubscriptionPlanResponse =
        initialState.getInboxAnalyzerSubscriptionPlanResponse;
      state.getInvoiceResponse = initialState.getInvoiceResponse;
      state.getInboxAnalyzerInvoiceResponse =
        initialState.getInboxAnalyzerInvoiceResponse;
      state.getInvoiceURLRequest = initialState.getInvoiceURLRequest;
      state.getInboxAnalyzerInvoiceURLRequest =
        initialState.getInboxAnalyzerInvoiceURLRequest;
      state.getPlansRequest = initialState.getPlansRequest;
      state.getDynamicCustomPlansRequest =
        initialState.getDynamicCustomPlansRequest;
      state.getLeadFinderPlansRequest = initialState.getLeadFinderPlansRequest;
      state.getInboxAnalyzerPlansRequest =
        initialState.getInboxAnalyzerPlansRequest;
      state.getLeadFinderPlansResponse =
        initialState.getLeadFinderPlansResponse;
      state.getInboxAnalyzerPlansResponse =
        initialState.getInboxAnalyzerPlansResponse;
      state.purchaseSubscriptionRequest =
        initialState.purchaseSubscriptionRequest;
      state.purchaseLeadFinderSubscriptionRequest =
        initialState.purchaseLeadFinderSubscriptionRequest;
      state.purchaseInboxAnalyzerSubscriptionRequest =
        initialState.purchaseInboxAnalyzerSubscriptionRequest;
      state.applyCouponCodeRequest = initialState.applyCouponCodeRequest;
      state.applyCouponCodeResponse = initialState.applyCouponCodeResponse;
      state.cancelEmailVerificationPlanRequest =
        initialState.cancelEmailVerificationPlanRequest;
      state.getEmailVerificationCreditsRequest =
        initialState.getEmailVerificationCreditsRequest;
      state.getEmailVerificationCreditsResponse =
        initialState.getEmailVerificationCreditsResponse;
      state.getEmailVerificationPlanRequest =
        initialState.getEmailVerificationPlanRequest;
      state.getEmailVerificationPlanResponse =
        initialState.getEmailVerificationPlanResponse;
      state.purchaseEmailVerificationCreditsRequest =
        initialState.purchaseEmailVerificationCreditsRequest;
      state.purchaseEmailVerificationCreditsResponse =
        initialState.purchaseEmailVerificationCreditsResponse;
      state.modifySubscriptionRequest = initialState.modifySubscriptionRequest;
      state.modifyInboxAnalyzerSubscriptionRequest =
        initialState.modifyInboxAnalyzerSubscriptionRequest;
      state.modifySubscriptionResponse =
        initialState.modifySubscriptionResponse;
      state.modifyInboxAnalyzerSubscriptionResponse =
        initialState.modifyInboxAnalyzerSubscriptionResponse;
      state.purchaseModifySubscriptionRequest =
        initialState.purchaseModifySubscriptionRequest;
      state.purchaseLeadFinderModifySubscriptionRequest =
        initialState.purchaseLeadFinderModifySubscriptionRequest;
      state.purchaseInboxAnalyzerModifySubscriptionRequest =
        initialState.purchaseInboxAnalyzerModifySubscriptionRequest;
      state.purchaseModifySubscriptionResponse =
        initialState.purchaseModifySubscriptionResponse;
      state.reactivateSubscriptionRequest =
        initialState.reactivateSubscriptionRequest;
      state.reactivateLeadFinderSubscriptionRequest =
        initialState.reactivateLeadFinderSubscriptionRequest;
      state.reactivateInboxAnalyzerSubscriptionRequest =
        initialState.reactivateInboxAnalyzerSubscriptionRequest;
      state.reactivateSubscriptionResponse =
        initialState.reactivateSubscriptionResponse;
      state.reactivateLeadFinderSubscriptionResponse =
        initialState.reactivateLeadFinderSubscriptionResponse;
      state.reactivateInboxAnalyzerSubscriptionResponse =
        initialState.reactivateInboxAnalyzerSubscriptionResponse;
      state.resumeSubscriptionRequest =
        initialState.reactivateSubscriptionRequest;
      state.resumeLeadFinderSubscriptionRequest =
        initialState.resumeLeadFinderSubscriptionRequest;
      state.resumeInboxAnalyzerSubscriptionRequest =
        initialState.resumeInboxAnalyzerSubscriptionRequest;
      state.resumeSubscriptionResponse =
        initialState.resumeSubscriptionResponse;
      state.resumeLeadFinderSubscriptionResponse =
        initialState.resumeLeadFinderSubscriptionResponse;
      state.resumeInboxAnalyzerSubscriptionResponse =
        initialState.resumeInboxAnalyzerSubscriptionResponse;
      state.revokePauseSubscriptionRequest =
        initialState.revokePauseSubscriptionRequest;
      state.handleSubscriptionRequest = initialState.handleSubscriptionRequest;
      state.handleLeadFinderChurnkeySubscriptionRequest =
        initialState.handleLeadFinderChurnkeySubscriptionRequest;
      state.handleInboxAnalyzerChurnkeySubscriptionRequest =
        initialState.handleInboxAnalyzerChurnkeySubscriptionRequest;
      state.handleSubscriptionResponse =
        initialState.handleSubscriptionResponse;
      state.handleLeadFinderChurnkeySubscriptionResponse =
        initialState.handleLeadFinderChurnkeySubscriptionResponse;
      state.handleInboxAnalyzerChurnkeySubscriptionResponse =
        initialState.handleInboxAnalyzerChurnkeySubscriptionResponse;

      state.purchaseLtdPlanRequest = initialState.purchaseLtdPlanRequest;
      state.upgradeLtdPlanRequest = initialState.upgradeLtdPlanRequest;
      state.modifyLtdPlanRequest = initialState.modifyLtdPlanRequest;
      state.purchaseLtdPlanResponse = initialState.purchaseLtdPlanResponse;
      state.upgradeLtdPlanResponse = initialState.upgradeLtdPlanResponse;
      state.modifyLtdPlanResponse = initialState.modifyLtdPlanResponse;

      state.calculatePayRequest = initialState.calculatePayRequest;
      state.getCalculatePayResponse = initialState.getCalculatePayResponse;

      state.calculateInboxAnalyzerPayRequest =
        initialState.calculateInboxAnalyzerPayRequest;
      state.getCalculateInboxAnalyzerPayResponse =
        initialState.getCalculateInboxAnalyzerPayResponse;

      state.getLeadFinderOneTimePlansRequest =
        initialState.getLeadFinderOneTimePlansRequest;
      state.purchaseLeadFinderOneTimePlanRequest =
        initialState.purchaseLeadFinderOneTimePlanRequest;
      state.getLeadFinderOneTimePlansResponse =
        initialState.getLeadFinderOneTimePlansResponse;

      //  AI Credits reseting states
      state.calculateAiCreditsPricingsRequest =
        initialState.calculateAiCreditsPricingsRequest;

      state.calculateCreditsPricesRequest =
        initialState.calculateCreditsPricesRequest;
      state.purchaseCreditsRequest = initialState.purchaseCreditsRequest;
      state.calculateCreditsPricesResponse =
        initialState.calculateCreditsPricesResponse;
      state.purchaseCreditsResponse = initialState.purchaseCreditsResponse;
      state.getCreditsTransactionStatusRequest =
        initialState.getCreditsTransactionStatusRequest;
      state.getCreditsTransactionStatusResponse =
        initialState.getCreditsTransactionStatusResponse;
    },

    /** we're using purchase email verification from different places.
     *  so we need to reset from every places.
     */
    resetPurchaseEmailVerificationCreditsResponse: (state) => {
      state.purchaseEmailVerificationCreditsRequest =
        initialState.purchaseEmailVerificationCreditsRequest;
      state.purchaseEmailVerificationCreditsResponse =
        initialState.purchaseEmailVerificationCreditsResponse;
    },

    resetPurchaseModifySubscriptionResponse: (state) => {
      state.purchaseModifySubscriptionRequest =
        initialState.purchaseModifySubscriptionRequest;
      state.purchaseModifySubscriptionResponse =
        initialState.purchaseModifySubscriptionResponse;
    },

    resetPurchaseSubscriptionRequestStatus: (state) => {
      state.purchaseSubscriptionRequest =
        initialState.purchaseSubscriptionRequest;
    },

    resetPurchaseLeadFinderSubscriptionRequestStatus: (state) => {
      state.purchaseLeadFinderSubscriptionRequest =
        initialState.purchaseLeadFinderSubscriptionRequest;
    },

    resetPurchaseInboxAnalyzerSubscriptionRequestStatus: (state) => {
      state.purchaseInboxAnalyzerSubscriptionRequest =
        initialState.purchaseInboxAnalyzerSubscriptionRequest;
    },

    resetPurchaseLeadFinderModifySubscriptionResponse: (state) => {
      state.purchaseLeadFinderModifySubscriptionRequest =
        initialState.purchaseLeadFinderModifySubscriptionRequest;
      state.purchaseLeadFinderModifySubscriptionResponse =
        initialState.purchaseLeadFinderModifySubscriptionResponse;
    },

    resetCancelLeadFinderAddonRequest: (state) => {
      state.cancelLeadFinderAddonRequest =
        initialState.cancelLeadFinderAddonRequest;
    },

    resetPurchaseInboxAnalyzerModifySubscriptionResponse: (state) => {
      state.purchaseInboxAnalyzerModifySubscriptionRequest =
        initialState.purchaseInboxAnalyzerModifySubscriptionRequest;
      state.purchaseInboxAnalyzerModifySubscriptionResponse =
        initialState.purchaseInboxAnalyzerModifySubscriptionResponse;
    },

    resetReactivateSubscriptionResponse: (state) => {
      state.reactivateSubscriptionRequest =
        initialState.reactivateSubscriptionRequest;
      state.reactivateSubscriptionResponse =
        initialState.reactivateSubscriptionResponse;
    },

    resetReactivateLeadFinderSubscriptionResponse: (state) => {
      state.reactivateLeadFinderSubscriptionRequest =
        initialState.reactivateLeadFinderSubscriptionRequest;
      state.reactivateLeadFinderSubscriptionResponse =
        initialState.reactivateLeadFinderSubscriptionResponse;
    },

    resetReactivateInboxAnalyzerSubscriptionResponse: (state) => {
      state.reactivateInboxAnalyzerSubscriptionRequest =
        initialState.reactivateInboxAnalyzerSubscriptionRequest;
      state.reactivateInboxAnalyzerSubscriptionResponse =
        initialState.reactivateInboxAnalyzerSubscriptionResponse;
    },

    resetResumeSubscriptionResponse: (state) => {
      state.resumeSubscriptionRequest = initialState.resumeSubscriptionRequest;
      state.resumeSubscriptionResponse =
        initialState.resumeSubscriptionResponse;
    },

    resetRevokePauseSubscriptionRequest: (state) => {
      state.revokePauseSubscriptionRequest =
        initialState.revokePauseSubscriptionRequest;
    },

    resetResumeLeadFinderSubscriptionResponse: (state) => {
      state.resumeLeadFinderSubscriptionRequest =
        initialState.resumeLeadFinderSubscriptionRequest;
      state.resumeLeadFinderSubscriptionResponse =
        initialState.resumeLeadFinderSubscriptionResponse;
    },

    resetResumeInboxAnalyzerSubscriptionResponse: (state) => {
      state.resumeInboxAnalyzerSubscriptionRequest =
        initialState.resumeInboxAnalyzerSubscriptionRequest;
      state.resumeInboxAnalyzerSubscriptionResponse =
        initialState.resumeInboxAnalyzerSubscriptionResponse;
    },

    resetHandleSubscriptionResponse: (state) => {
      state.handleSubscriptionRequest = initialState.handleSubscriptionRequest;
      state.handleSubscriptionResponse =
        initialState.handleSubscriptionResponse;
    },

    resetHandleLeadFinderSubscriptionChurnkeyResponse: (state) => {
      state.handleLeadFinderChurnkeySubscriptionRequest =
        initialState.handleLeadFinderChurnkeySubscriptionRequest;
      state.handleLeadFinderChurnkeySubscriptionResponse =
        initialState.handleLeadFinderChurnkeySubscriptionResponse;
    },

    resetHandleInboxAnalyzerSubscriptionChurnkeyResponse: (state) => {
      state.handleInboxAnalyzerChurnkeySubscriptionRequest =
        initialState.handleInboxAnalyzerChurnkeySubscriptionRequest;
      state.handleInboxAnalyzerChurnkeySubscriptionResponse =
        initialState.handleInboxAnalyzerChurnkeySubscriptionResponse;
    },

    resetModifySubscriptionResponse: (state) => {
      state.modifySubscriptionRequest = initialState.modifySubscriptionRequest;
      state.modifySubscriptionResponse =
        initialState.modifySubscriptionResponse;
    },

    resetInboxAnalyzerModifySubscriptionResponse: (state) => {
      state.modifyInboxAnalyzerSubscriptionRequest =
        initialState.modifyInboxAnalyzerSubscriptionRequest;
      state.modifyInboxAnalyzerSubscriptionResponse =
        initialState.modifyInboxAnalyzerSubscriptionResponse;
    },

    deleteAppliedCouponCode: (state) => {
      state.applyCouponCodeResponse = initialState.applyCouponCodeResponse;
      state.applyCouponCodeRequest = initialState.applyCouponCodeRequest;
    },
    resetConnectedUsersAndEmailAccountsRequest: (state) => {
      state.getConnectedUsersAndEmailAccountsRequest =
        initialState.getConnectedUsersAndEmailAccountsRequest;
    },

    // Reset Calculate Pay State
    resetCalculatePayState: (state) => {
      state.calculatePayRequest = initialState.calculatePayRequest;
      state.getCalculatePayResponse = initialState.getCalculatePayResponse;
    },

    // Reset Calculate Inbox Analyzer Pay State
    resetCalculateInboxAnalyzerPayState: (state) => {
      state.calculateInboxAnalyzerPayRequest =
        initialState.calculateInboxAnalyzerPayRequest;
      state.getCalculateInboxAnalyzerPayResponse =
        initialState.getCalculateInboxAnalyzerPayResponse;
    },

    // Reset Purchase LTD Plan State
    resetPurchaseLtdPlan: (state) => {
      state.purchaseLtdPlanRequest = initialState.purchaseLtdPlanRequest;
      state.purchaseLtdPlanResponse = initialState.purchaseLtdPlanResponse;
    },

    // Reset Upgrade LTD Plan State
    resetUpgradeLtdPlan: (state) => {
      state.upgradeLtdPlanRequest = initialState.upgradeLtdPlanRequest;
      state.upgradeLtdPlanResponse = initialState.upgradeLtdPlanResponse;
    },

    // Reset Modify LTD Plan State
    resetModifyLtdPlan: (state) => {
      state.modifyLtdPlanRequest = initialState.modifyLtdPlanRequest;
      state.modifyLtdPlanResponse = initialState.modifyLtdPlanResponse;
    },

    // Store the select plan details
    selectPlan: (state, action: PayloadAction<SelectedPlanDetails>) => {
      state.selectedPlanDetails = action.payload;
    },

    // Reset Invoice State
    resetInvoice: (state) => {
      state.getInvoiceURLRequest = initialState.getInvoiceURLRequest;
      state.getInvoiceResponse = initialState.getInvoiceResponse;
    },
    resetRenewDomainsResponse: (state) => {
      state.renewDomainsResponse = initialState.renewDomainsResponse;
    },
    resetInboxAnalyzerInvoice: (state) => {
      state.getInboxAnalyzerInvoiceURLRequest =
        initialState.getInboxAnalyzerInvoiceURLRequest;
      state.getInboxAnalyzerInvoiceResponse =
        initialState.getInboxAnalyzerInvoiceResponse;
    },

    // Reset Purchase Details
    resetPurchaseDetails: (state) => {
      state.purchaseDetailsById = initialState.purchaseDetailsById;
    },

    // Reset Export Credit History
    resetExportCreditHistoryRequest: (state) => {
      state.exportCreditHistoryRequest =
        initialState.exportCreditHistoryRequest;
    },
    resetAiCreditHistoryRequest: (state) => {
      state.aiCreditHistoryRequest = initialState.aiCreditHistoryRequest;
    },

    // Reset Purchase Lead Finder Plan
    resetPurchaseLeadFinderOneTimePlan: (state) => {
      state.purchaseLeadFinderOneTimePlanRequest =
        initialState.purchaseLeadFinderOneTimePlanRequest;
      state.purchaseLeadFinderOneTimePlanResponse =
        initialState.purchaseLeadFinderOneTimePlanResponse;
    },

    // Reset Schedule Upgrade Plan Request
    resetSchedulePlanUpgradeRequest: (state) => {
      state.schedulePlanUpgradeRequest =
        initialState.schedulePlanUpgradeRequest;
    },

    // Reset Calculate Credits Prices Request
    resetCalculateCreditsPricesRequest: (state) => {
      state.calculateCreditsPricesRequest =
        initialState.calculateCreditsPricesRequest;
      state.calculateCreditsPricesResponse =
        initialState.calculateCreditsPricesResponse;
    },

    // Reset Purchase Credits Request
    resetPurchaseCreditsRequest: (state) => {
      state.purchaseCreditsRequest = initialState.purchaseCreditsRequest;
      state.purchaseCreditsResponse = initialState.purchaseCreditsResponse;
    },

    // Reset Get Credits Transaction Status Request
    resetGetCreditsTransactionStatusRequest: (state) => {
      state.getCreditsTransactionStatusRequest =
        initialState.getCreditsTransactionStatusRequest;
      state.getCreditsTransactionStatusResponse =
        initialState.getCreditsTransactionStatusResponse;
    },

    // Paddle Polling reducers
    startPaddlePolling: (state, action: PayloadAction<PaddleApplicationType>) => {
      state.paddlePolling = {
        isActive: true,
        applicationType: action.payload,
        pollingCount: 0,
        status: 'polling',
      };
    },
    stopPaddlePolling: (state) => {
      state.paddlePolling = initialState.paddlePolling;
    },
    incrementPaddlePollingCount: (state) => {
      state.paddlePolling.pollingCount += 1;
    },
    setPaddlePollingStatus: (state, action: PayloadAction<PaddlePollingState['status']>) => {
      state.paddlePolling.status = action.payload;
      if (action.payload !== 'polling') {
        state.paddlePolling.isActive = false;
      }
    },
  },
  extraReducers: (builder) => {
    // Get account subscription detail
    builder.addCase(getAccountSubscriptionRequest.pending, (state) => {
      state.getAccountSubscriptionRequest.status = RequestStatus.Pending;
      state.getAccountSubscriptionRequest.message = null;
      state.getAccountSubscriptionRequest.error = null;
    });
    builder.addCase(getAccountSubscriptionPlanRequest.pending, (state) => {
      state.getAccountSubscriptionPlanRequest.status = RequestStatus.Pending;
      state.getAccountSubscriptionPlanRequest.message = null;
      state.getAccountSubscriptionPlanRequest.error = null;
    });
    builder.addCase(
      getAccountSubscriptionRequest.fulfilled,
      (state, action) => {
        state.getAccountSubscriptionRequest.status = RequestStatus.Succeeded;
        state.getAccountSubscriptionResponse = action.payload.payload;
        state.getAccountSubscriptionResponse.nextBillingAt = state.getAccountSubscriptionResponse.nextBillingAt?.replace(
          /-/g,
          '/',
        );
        state.getAccountSubscriptionResponse.startAt = state.getAccountSubscriptionResponse.startAt?.replace(
          /-/g,
          '/',
        );
        state.getAccountSubscriptionRequest.message = null;
        state.getAccountSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      getAccountSubscriptionPlanRequest.fulfilled,
      (state, action) => {
        state.getAccountSubscriptionPlanRequest.status =
          RequestStatus.Succeeded;
        state.getAccountSubscriptionPlanResponse = action.payload.payload;
        state.getAccountSubscriptionPlanResponse.startAt = state.getAccountSubscriptionPlanResponse.startAt?.replace(
          /-/g,
          '/',
        );
        state.getAccountSubscriptionPlanResponse.endAt = state.getAccountSubscriptionPlanResponse.endAt?.replace(
          /-/g,
          '/',
        );
        // state.getAccountSubscriptionPlanResponse.message = null;
        // state.getAccountSubscriptionPlanResponse.error = null;
      },
    );
    builder.addCase(getAccountSubscriptionRequest.rejected, (state, action) => {
      state.getAccountSubscriptionRequest.status = RequestStatus.Failed;
      state.getAccountSubscriptionRequest.message = action.payload.message;
      state.getAccountSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });
    builder.addCase(
      getAccountSubscriptionPlanRequest.rejected,
      (state, action) => {
        state.getAccountSubscriptionRequest.status = RequestStatus.Failed;
        state.getAccountSubscriptionRequest.message = action.payload.message;
        state.getAccountSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Lead Finder subscription detail

    builder.addCase(getLeadFinderSubscriptionRequest.pending, (state) => {
      state.getLeadFinderSubscriptionRequest.status = RequestStatus.Pending;
      state.getLeadFinderSubscriptionRequest.message = null;
      state.getLeadFinderSubscriptionRequest.error = null;
    });
    builder.addCase(
      getLeadFinderSubscriptionRequest.fulfilled,
      (state, action) => {
        state.getLeadFinderSubscriptionRequest.status = RequestStatus.Succeeded;
        state.getLeadFinderSubscriptionResponse = action.payload.payload;
        state.getLeadFinderSubscriptionResponse.nextBillingAt = state.getLeadFinderSubscriptionResponse.nextBillingAt?.replace(
          /-/g,
          '/',
        );
        state.getLeadFinderSubscriptionResponse.startAt = state.getLeadFinderSubscriptionResponse.startAt?.replace(
          /-/g,
          '/',
        );
        state.getLeadFinderSubscriptionRequest.message = null;
        state.getLeadFinderSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      getLeadFinderSubscriptionRequest.rejected,
      (state, action) => {
        state.getLeadFinderSubscriptionRequest.status = RequestStatus.Failed;
        state.getLeadFinderSubscriptionRequest.message = action.payload.message;
        state.getLeadFinderSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Inbox Analyzer subscription detail
    builder.addCase(getInboxAnalyzerSubscriptionRequest.pending, (state) => {
      state.getInboxAnalyzerSubscriptionRequest.status = RequestStatus.Pending;
      state.getInboxAnalyzerSubscriptionRequest.message = null;
      state.getInboxAnalyzerSubscriptionRequest.error = null;
    });
    builder.addCase(
      getInboxAnalyzerSubscriptionPlanRequest.pending,
      (state) => {
        state.getInboxAnalyzerSubscriptionPlanRequest.status =
          RequestStatus.Pending;
        state.getInboxAnalyzerSubscriptionPlanRequest.message = null;
        state.getInboxAnalyzerSubscriptionPlanRequest.error = null;
      },
    );
    builder.addCase(
      getInboxAnalyzerSubscriptionRequest.fulfilled,
      (state, action) => {
        state.getInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.getInboxAnalyzerSubscriptionResponse = action.payload.payload;
        state.getInboxAnalyzerSubscriptionResponse.nextBillingAt = state?.getInboxAnalyzerSubscriptionResponse?.nextBillingAt?.replace(
          /-/g,
          '/',
        );
        state.getInboxAnalyzerSubscriptionResponse.startAt = state?.getInboxAnalyzerSubscriptionResponse?.startAt?.replace(
          /-/g,
          '/',
        );
        state.getInboxAnalyzerSubscriptionRequest.message = null;
        state.getInboxAnalyzerSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      getInboxAnalyzerSubscriptionPlanRequest.fulfilled,
      (state, action) => {
        state.getInboxAnalyzerSubscriptionPlanRequest.status =
          RequestStatus.Succeeded;
        state.getInboxAnalyzerSubscriptionPlanResponse = action.payload.payload;
        state.getInboxAnalyzerSubscriptionPlanResponse.startAt = state.getInboxAnalyzerSubscriptionPlanResponse.startAt?.replace(
          /-/g,
          '/',
        );
        state.getInboxAnalyzerSubscriptionPlanResponse.endAt = state.getInboxAnalyzerSubscriptionPlanResponse.endAt?.replace(
          /-/g,
          '/',
        );
        // state.getAccountSubscriptionPlanResponse.message = null;
        // state.getAccountSubscriptionPlanResponse.error = null;
      },
    );
    builder.addCase(
      getInboxAnalyzerSubscriptionRequest.rejected,
      (state, action) => {
        state.getInboxAnalyzerSubscriptionRequest.status = RequestStatus.Failed;
        state.getInboxAnalyzerSubscriptionRequest.message =
          action.payload.message;
        state.getInboxAnalyzerSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    builder.addCase(
      getInboxAnalyzerSubscriptionPlanRequest.rejected,
      (state, action) => {
        state.getInboxAnalyzerSubscriptionRequest.status = RequestStatus.Failed;
        state.getInboxAnalyzerSubscriptionRequest.message =
          action.payload.message;
        state.getInboxAnalyzerSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get invoice-url request
    builder.addCase(getInvoiceURLRequest.pending, (state) => {
      state.getInvoiceURLRequest.status = RequestStatus.Pending;
      state.getInvoiceURLRequest.message = null;
      state.getInvoiceURLRequest.error = null;
    });
    builder.addCase(getInvoiceURLRequest.fulfilled, (state, action) => {
      state.getInvoiceURLRequest.status = RequestStatus.Succeeded;
      state.getInvoiceResponse = action.payload.payload;
      state.getInvoiceURLRequest.message = null;
      state.getInvoiceURLRequest.error = null;
    });
    builder.addCase(getInvoiceURLRequest.rejected, (state, action) => {
      state.getInvoiceURLRequest.status = RequestStatus.Failed;
      state.getInvoiceURLRequest.message = action.payload.message;
      state.getInvoiceURLRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get inbox analyzer invoice-url request
    builder.addCase(getInboxAnalyzerInvoiceURLRequest.pending, (state) => {
      state.getInboxAnalyzerInvoiceURLRequest.status = RequestStatus.Pending;
      state.getInboxAnalyzerInvoiceURLRequest.message = null;
      state.getInboxAnalyzerInvoiceURLRequest.error = null;
    });
    builder.addCase(
      getInboxAnalyzerInvoiceURLRequest.fulfilled,
      (state, action) => {
        state.getInboxAnalyzerInvoiceURLRequest.status =
          RequestStatus.Succeeded;
        state.getInboxAnalyzerInvoiceResponse = action.payload.payload;
        state.getInboxAnalyzerInvoiceURLRequest.message = null;
        state.getInboxAnalyzerInvoiceURLRequest.error = null;
      },
    );
    builder.addCase(
      getInboxAnalyzerInvoiceURLRequest.rejected,
      (state, action) => {
        state.getInboxAnalyzerInvoiceURLRequest.status = RequestStatus.Failed;
        state.getInboxAnalyzerInvoiceURLRequest.message =
          action.payload.message;
        state.getInboxAnalyzerInvoiceURLRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Purchase Subscription request
    builder.addCase(purchaseSubscriptionRequest.pending, (state) => {
      state.purchaseSubscriptionRequest.status = RequestStatus.Pending;
      state.purchaseSubscriptionRequest.message = null;
      state.purchaseSubscriptionRequest.error = null;
    });
    builder.addCase(purchaseSubscriptionRequest.fulfilled, (state, action) => {
      state.purchaseSubscriptionRequest.status = RequestStatus.Succeeded;
      state.purchaseSubscriptionRequest.message =
        action.payload.payload.message;
      state.purchaseSubscriptionRequest.error = null;
    });
    builder.addCase(purchaseSubscriptionRequest.rejected, (state, action) => {
      state.purchaseSubscriptionRequest.status = RequestStatus.Failed;
      state.purchaseSubscriptionRequest.message = action.payload.message;
      state.purchaseSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Purchase Lead Finder Subscription request
    builder.addCase(purchaseLeadFinderSubscriptionRequest.pending, (state) => {
      state.purchaseLeadFinderSubscriptionRequest.status =
        RequestStatus.Pending;
      state.purchaseLeadFinderSubscriptionRequest.message = null;
      state.purchaseLeadFinderSubscriptionRequest.error = null;
    });
    builder.addCase(
      purchaseLeadFinderSubscriptionRequest.fulfilled,
      (state, action) => {
        state.purchaseLeadFinderSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.purchaseLeadFinderSubscriptionRequest.message =
          action.payload.payload.message;
        state.purchaseLeadFinderSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseLeadFinderSubscriptionRequest.rejected,
      (state, action) => {
        state.purchaseLeadFinderSubscriptionRequest.status =
          RequestStatus.Failed;
        state.purchaseLeadFinderSubscriptionRequest.message =
          action.payload.message;
        state.purchaseLeadFinderSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Purchase Inbox Analyzer Subscription request
    builder.addCase(
      purchaseInboxAnalyzerSubscriptionRequest.pending,
      (state) => {
        state.purchaseInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Pending;
        state.purchaseInboxAnalyzerSubscriptionRequest.message = null;
        state.purchaseInboxAnalyzerSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseInboxAnalyzerSubscriptionRequest.fulfilled,
      (state, action) => {
        state.purchaseInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.purchaseInboxAnalyzerSubscriptionRequest.message =
          action.payload.payload.message;
        state.purchaseInboxAnalyzerSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseInboxAnalyzerSubscriptionRequest.rejected,
      (state, action) => {
        state.purchaseInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Failed;
        state.purchaseInboxAnalyzerSubscriptionRequest.message =
          action.payload.message;
        state.purchaseInboxAnalyzerSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Apply coupon code request
    builder.addCase(applyCouponCodeRequest.pending, (state) => {
      state.applyCouponCodeRequest.status = RequestStatus.Pending;
      state.applyCouponCodeRequest.message = null;
      state.applyCouponCodeRequest.error = null;
    });
    builder.addCase(applyCouponCodeRequest.fulfilled, (state, action) => {
      state.applyCouponCodeRequest.status = RequestStatus.Succeeded;
      state.applyCouponCodeRequest.message = action.payload.payload.message;
      state.applyCouponCodeRequest.error = null;
      state.applyCouponCodeResponse = action.payload.payload;
    });
    builder.addCase(applyCouponCodeRequest.rejected, (state, action) => {
      state.applyCouponCodeRequest.status = RequestStatus.Failed;
      state.applyCouponCodeRequest.message = action.payload.message;
      state.applyCouponCodeRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get plans request
    builder.addCase(getPlansRequest.pending, (state) => {
      state.getPlansRequest.status = RequestStatus.Pending;
      state.getPlansRequest.message = null;
      state.getPlansRequest.error = null;
    });
    builder.addCase(getPlansRequest.fulfilled, (state, action) => {
      state.getPlansRequest.status = RequestStatus.Succeeded;
      state.getPlansResponse = action.payload.payload.rows;
      state.getPlansRequest.message = null;
      state.getPlansRequest.error = null;
    });
    builder.addCase(getPlansRequest.rejected, (state, action) => {
      state.getPlansRequest.status = RequestStatus.Failed;
      state.getPlansRequest.message = action.payload.message;
      state.getPlansRequest.error = !action.payload.isHandled && action.payload;
    });

    // Get plans request
    builder.addCase(dynamicCustomPlansRequest.pending, (state) => {
      state.getDynamicCustomPlansRequest.status = RequestStatus.Pending;
      state.getDynamicCustomPlansRequest.message = null;
      state.getDynamicCustomPlansRequest.error = null;
    });
    builder.addCase(dynamicCustomPlansRequest.fulfilled, (state, action) => {
      state.getDynamicCustomPlansRequest.status = RequestStatus.Succeeded;
      state.getDynamicCustomPlansResponse = action.payload.payload;
      state.getDynamicCustomPlansRequest.message =
        action.payload.payload.message;
      state.getDynamicCustomPlansRequest.error = null;
    });
    builder.addCase(dynamicCustomPlansRequest.rejected, (state, action) => {
      state.getDynamicCustomPlansRequest.status = RequestStatus.Failed;
      state.getDynamicCustomPlansRequest.message = action.payload.message;
      state.getDynamicCustomPlansRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Lead Finder plans request
    builder.addCase(getLeadFinderPlansRequest.pending, (state) => {
      state.getLeadFinderPlansRequest.status = RequestStatus.Pending;
      state.getLeadFinderPlansRequest.message = null;
      state.getLeadFinderPlansRequest.error = null;
    });
    builder.addCase(getLeadFinderPlansRequest.fulfilled, (state, action) => {
      state.getLeadFinderPlansRequest.status = RequestStatus.Succeeded;
      state.getLeadFinderPlansResponse = action.payload.payload;
      state.getLeadFinderPlansRequest.message = null;
      state.getLeadFinderPlansRequest.error = null;
    });
    builder.addCase(getLeadFinderPlansRequest.rejected, (state, action) => {
      state.getLeadFinderPlansRequest.status = RequestStatus.Failed;
      state.getLeadFinderPlansRequest.message = action.payload.message;
      state.getLeadFinderPlansRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Inbox Analyzer plans request
    builder.addCase(getInboxAnalyzerPlansRequest.pending, (state) => {
      state.getInboxAnalyzerPlansRequest.status = RequestStatus.Pending;
      state.getInboxAnalyzerPlansRequest.message = null;
      state.getInboxAnalyzerPlansRequest.error = null;
    });
    builder.addCase(getInboxAnalyzerPlansRequest.fulfilled, (state, action) => {
      state.getInboxAnalyzerPlansRequest.status = RequestStatus.Succeeded;
      state.getInboxAnalyzerPlansResponse = action.payload.payload.rows;
      state.getInboxAnalyzerPlansRequest.message = null;
      state.getInboxAnalyzerPlansRequest.error = null;
    });
    builder.addCase(getInboxAnalyzerPlansRequest.rejected, (state, action) => {
      state.getInboxAnalyzerPlansRequest.status = RequestStatus.Failed;
      state.getInboxAnalyzerPlansRequest.message = action.payload.message;
      state.getInboxAnalyzerPlansRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get connected users and email accounts
    builder.addCase(
      getConnectedUsersAndEmailAccountsRequest.pending,
      (state) => {
        state.getConnectedUsersAndEmailAccountsRequest.status =
          RequestStatus.Pending;
        state.getConnectedUsersAndEmailAccountsRequest.message = null;
        state.getConnectedUsersAndEmailAccountsRequest.error = null;
      },
    );
    builder.addCase(
      getConnectedUsersAndEmailAccountsRequest.fulfilled,
      (state, action) => {
        state.getConnectedUsersAndEmailAccountsRequest.status =
          RequestStatus.Succeeded;
        // eslint-disable-next-line prefer-destructuring
        state.getConnectedUsersAndEmailAccountsResponse =
          action.payload.payload[0];
        state.getConnectedUsersAndEmailAccountsRequest.message = null;
        state.getConnectedUsersAndEmailAccountsRequest.error = null;
      },
    );
    builder.addCase(
      getConnectedUsersAndEmailAccountsRequest.rejected,
      (state, action) => {
        state.getConnectedUsersAndEmailAccountsRequest.status =
          RequestStatus.Failed;
        state.getConnectedUsersAndEmailAccountsRequest.message =
          action.payload.message;
        state.getConnectedUsersAndEmailAccountsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get email verification credits request
    builder.addCase(getEmailVerificationCreditsRequest.pending, (state) => {
      state.getEmailVerificationCreditsRequest.status = RequestStatus.Pending;
      state.getEmailVerificationCreditsRequest.message = null;
      state.getEmailVerificationCreditsRequest.error = null;
    });
    builder.addCase(
      getEmailVerificationCreditsRequest.fulfilled,
      (state, action) => {
        state.getEmailVerificationCreditsRequest.status =
          RequestStatus.Succeeded;
        state.getEmailVerificationCreditsResponse = action.payload.payload;
        state.getEmailVerificationCreditsRequest.message = null;
        state.getEmailVerificationCreditsRequest.error = null;
      },
    );
    builder.addCase(
      getEmailVerificationCreditsRequest.rejected,
      (state, action) => {
        state.getEmailVerificationCreditsRequest.status = RequestStatus.Failed;
        state.getEmailVerificationCreditsRequest.message =
          action.payload.message;
        state.getEmailVerificationCreditsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get email verification plans request
    builder.addCase(getEmailVerificationPlanRequest.pending, (state) => {
      state.getEmailVerificationPlanRequest.status = RequestStatus.Pending;
      state.getEmailVerificationPlanRequest.message = null;
      state.getEmailVerificationPlanRequest.error = null;
    });
    builder.addCase(
      getEmailVerificationPlanRequest.fulfilled,
      (state, action) => {
        state.getEmailVerificationPlanRequest.status = RequestStatus.Succeeded;
        state.getEmailVerificationPlanResponse = action.payload.payload.rows;
        state.getEmailVerificationPlanRequest.message = null;
        state.getEmailVerificationPlanRequest.error = null;
      },
    );
    builder.addCase(
      getEmailVerificationPlanRequest.rejected,
      (state, action) => {
        state.getEmailVerificationPlanRequest.status = RequestStatus.Failed;
        state.getEmailVerificationPlanRequest.message = action.payload.message;
        state.getEmailVerificationPlanRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Purchase email verification credits request
    builder.addCase(
      purchaseEmailVerificationCreditsRequest.pending,
      (state) => {
        state.purchaseEmailVerificationCreditsRequest.status =
          RequestStatus.Pending;
        state.purchaseEmailVerificationCreditsRequest.message = null;
        state.purchaseEmailVerificationCreditsRequest.error = null;
      },
    );
    builder.addCase(
      purchaseEmailVerificationCreditsRequest.fulfilled,
      (state, action) => {
        state.purchaseEmailVerificationCreditsRequest.status =
          RequestStatus.Succeeded;
        state.purchaseEmailVerificationCreditsResponse = action.payload.payload;
        state.purchaseEmailVerificationCreditsRequest.message =
          action.payload.payload.message;
        state.purchaseEmailVerificationCreditsRequest.error = null;
      },
    );
    builder.addCase(
      purchaseEmailVerificationCreditsRequest.rejected,
      (state, action) => {
        state.purchaseEmailVerificationCreditsRequest.status =
          RequestStatus.Failed;
        state.purchaseEmailVerificationCreditsRequest.message =
          action.payload.message;
        state.purchaseEmailVerificationCreditsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Modify subscription request
    builder.addCase(modifySubscriptionRequest.pending, (state) => {
      state.modifySubscriptionRequest.status = RequestStatus.Pending;
      state.modifySubscriptionRequest.message = null;
      state.modifySubscriptionRequest.error = null;
    });
    builder.addCase(modifySubscriptionRequest.fulfilled, (state, action) => {
      state.modifySubscriptionRequest.status = RequestStatus.Succeeded;
      state.modifySubscriptionRequest.message = action.payload.payload.message;
      state.modifySubscriptionRequest.error = null;
      state.modifySubscriptionResponse = action.payload.payload;
    });
    builder.addCase(modifySubscriptionRequest.rejected, (state, action) => {
      state.modifySubscriptionRequest.status = RequestStatus.Failed;
      state.modifySubscriptionRequest.message = action.payload.message;
      state.modifySubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Modify Inbox Analyzer subscription request
    builder.addCase(modifyInboxAnalyzerSubscriptionRequest.pending, (state) => {
      state.modifyInboxAnalyzerSubscriptionRequest.status =
        RequestStatus.Pending;
      state.modifyInboxAnalyzerSubscriptionRequest.message = null;
      state.modifyInboxAnalyzerSubscriptionRequest.error = null;
    });
    builder.addCase(
      modifyInboxAnalyzerSubscriptionRequest.fulfilled,
      (state, action) => {
        state.modifyInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.modifyInboxAnalyzerSubscriptionRequest.message =
          action.payload.payload.message;
        state.modifyInboxAnalyzerSubscriptionRequest.error = null;
        state.modifyInboxAnalyzerSubscriptionResponse = action.payload.payload;
      },
    );
    builder.addCase(
      modifyInboxAnalyzerSubscriptionRequest.rejected,
      (state, action) => {
        state.modifyInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Failed;
        state.modifyInboxAnalyzerSubscriptionRequest.message =
          action.payload.message;
        state.modifyInboxAnalyzerSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Purchase modify subscription request
    builder.addCase(purchaseModifySubscriptionRequest.pending, (state) => {
      state.purchaseModifySubscriptionRequest.status = RequestStatus.Pending;
      state.purchaseModifySubscriptionRequest.message = null;
      state.purchaseModifySubscriptionRequest.error = null;
    });
    builder.addCase(
      purchaseModifySubscriptionRequest.fulfilled,
      (state, action) => {
        state.purchaseModifySubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.purchaseModifySubscriptionResponse = action.payload?.payload;
        state.purchaseModifySubscriptionRequest.message = null;
        state.purchaseModifySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseModifySubscriptionRequest.rejected,
      (state, action) => {
        state.purchaseModifySubscriptionRequest.status = RequestStatus.Failed;
        state.purchaseModifySubscriptionRequest.message =
          action.payload.message;
        state.purchaseModifySubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Purchase Lead Finder modify subscription request
    builder.addCase(
      purchaseLeadFinderModifySubscriptionRequest.pending,
      (state) => {
        state.purchaseLeadFinderModifySubscriptionRequest.status =
          RequestStatus.Pending;
        state.purchaseLeadFinderModifySubscriptionRequest.message = null;
        state.purchaseLeadFinderModifySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseLeadFinderModifySubscriptionRequest.fulfilled,
      (state, action) => {
        state.purchaseLeadFinderModifySubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.purchaseLeadFinderModifySubscriptionResponse =
          action.payload?.payload;
        state.purchaseLeadFinderModifySubscriptionRequest.message =
          action.payload.payload.message;
        state.purchaseLeadFinderModifySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseLeadFinderModifySubscriptionRequest.rejected,
      (state, action) => {
        state.purchaseLeadFinderModifySubscriptionRequest.status =
          RequestStatus.Failed;
        state.purchaseLeadFinderModifySubscriptionRequest.message =
          action.payload.message;
        state.purchaseLeadFinderModifySubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Cancel Lead Finder addon request
    builder.addCase(cancelLeadFinderAddonRequest.pending, (state) => {
      state.cancelLeadFinderAddonRequest.status = RequestStatus.Pending;
      state.cancelLeadFinderAddonRequest.message = null;
      state.cancelLeadFinderAddonRequest.error = null;
    });
    builder.addCase(
      cancelLeadFinderAddonRequest.fulfilled,
      (state, action) => {
        state.cancelLeadFinderAddonRequest.status = RequestStatus.Succeeded;
        state.cancelLeadFinderAddonRequest.message =
          action.payload?.payload?.message || 'Add-on cancelled successfully.';
        state.cancelLeadFinderAddonRequest.error = null;
      },
    );
    builder.addCase(
      cancelLeadFinderAddonRequest.rejected,
      (state, action) => {
        state.cancelLeadFinderAddonRequest.status = RequestStatus.Failed;
        state.cancelLeadFinderAddonRequest.message =
          action.payload.message;
        state.cancelLeadFinderAddonRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Purchase Inbox Analyzer modify subscription request
    builder.addCase(
      purchaseInboxAnalyzerModifySubscriptionRequest.pending,
      (state) => {
        state.purchaseInboxAnalyzerModifySubscriptionRequest.status =
          RequestStatus.Pending;
        state.purchaseInboxAnalyzerModifySubscriptionRequest.message = null;
        state.purchaseInboxAnalyzerModifySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseInboxAnalyzerModifySubscriptionRequest.fulfilled,
      (state, action) => {
        state.purchaseInboxAnalyzerModifySubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.purchaseInboxAnalyzerModifySubscriptionResponse =
          action.payload?.payload;
        state.purchaseInboxAnalyzerModifySubscriptionRequest.message =
          action.payload.payload.message;
        state.purchaseInboxAnalyzerModifySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      purchaseInboxAnalyzerModifySubscriptionRequest.rejected,
      (state, action) => {
        state.purchaseInboxAnalyzerModifySubscriptionRequest.status =
          RequestStatus.Failed;
        state.purchaseInboxAnalyzerModifySubscriptionRequest.message =
          action.payload.message;
        state.purchaseInboxAnalyzerModifySubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Reactivate subscription request
    builder.addCase(reactivateSubscriptionRequest.pending, (state) => {
      state.reactivateSubscriptionRequest.status = RequestStatus.Pending;
      state.reactivateSubscriptionRequest.message = null;
      state.reactivateSubscriptionRequest.error = null;
    });
    builder.addCase(
      reactivateSubscriptionRequest.fulfilled,
      (state, action) => {
        state.reactivateSubscriptionRequest.status = RequestStatus.Succeeded;
        state.reactivateSubscriptionResponse = action.payload.payload;
        state.reactivateSubscriptionRequest.message = null;
        state.reactivateSubscriptionRequest.error = null;
      },
    );
    builder.addCase(reactivateSubscriptionRequest.rejected, (state, action) => {
      state.reactivateSubscriptionRequest.status = RequestStatus.Failed;
      state.reactivateSubscriptionRequest.message = action.payload.message;
      state.reactivateSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Reactivate Lead Finder subscription request
    builder.addCase(
      reactivateLeadFinderSubscriptionRequest.pending,
      (state) => {
        state.reactivateLeadFinderSubscriptionRequest.status =
          RequestStatus.Pending;
        state.reactivateLeadFinderSubscriptionRequest.message = null;
        state.reactivateLeadFinderSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      reactivateLeadFinderSubscriptionRequest.fulfilled,
      (state, action) => {
        state.reactivateLeadFinderSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.reactivateLeadFinderSubscriptionResponse = action.payload.payload;
        state.reactivateLeadFinderSubscriptionRequest.message = null;
        state.reactivateLeadFinderSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      reactivateLeadFinderSubscriptionRequest.rejected,
      (state, action) => {
        state.reactivateLeadFinderSubscriptionRequest.status =
          RequestStatus.Failed;
        state.reactivateLeadFinderSubscriptionRequest.message =
          action.payload.message;
        state.reactivateLeadFinderSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Reactivate Inbox Analyzer subscription request
    builder.addCase(
      reactivateInboxAnalyzerSubscriptionRequest.pending,
      (state) => {
        state.reactivateInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Pending;
        state.reactivateInboxAnalyzerSubscriptionRequest.message = null;
        state.reactivateInboxAnalyzerSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      reactivateInboxAnalyzerSubscriptionRequest.fulfilled,
      (state, action) => {
        state.reactivateInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.reactivateInboxAnalyzerSubscriptionResponse =
          action.payload.payload;
        state.reactivateInboxAnalyzerSubscriptionRequest.message = null;
        state.reactivateInboxAnalyzerSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      reactivateInboxAnalyzerSubscriptionRequest.rejected,
      (state, action) => {
        state.reactivateInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Failed;
        state.reactivateInboxAnalyzerSubscriptionRequest.message =
          action.payload.message;
        state.reactivateInboxAnalyzerSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Resume subscription request
    builder.addCase(resumeSubscriptionRequest.pending, (state) => {
      state.resumeSubscriptionRequest.status = RequestStatus.Pending;
      state.resumeSubscriptionRequest.message = null;
      state.resumeSubscriptionRequest.error = null;
    });
    builder.addCase(resumeSubscriptionRequest.fulfilled, (state, action) => {
      state.resumeSubscriptionRequest.status = RequestStatus.Succeeded;
      state.resumeSubscriptionResponse = action.payload?.payload;
      state.resumeSubscriptionRequest.message = null;
      state.resumeSubscriptionRequest.error = null;
    });
    builder.addCase(resumeSubscriptionRequest.rejected, (state, action) => {
      state.resumeSubscriptionRequest.status = RequestStatus.Failed;
      state.resumeSubscriptionRequest.message = action.payload.message;
      state.resumeSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Resume Lead Finder subscription request
    builder.addCase(resumeLeadFinderSubscriptionRequest.pending, (state) => {
      state.resumeLeadFinderSubscriptionRequest.status = RequestStatus.Pending;
      state.resumeLeadFinderSubscriptionRequest.message = null;
      state.resumeLeadFinderSubscriptionRequest.error = null;
    });
    builder.addCase(
      resumeLeadFinderSubscriptionRequest.fulfilled,
      (state, action) => {
        state.resumeLeadFinderSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.resumeLeadFinderSubscriptionResponse = action.payload?.payload;
        state.resumeLeadFinderSubscriptionRequest.message = null;
        state.resumeLeadFinderSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      resumeLeadFinderSubscriptionRequest.rejected,
      (state, action) => {
        state.resumeLeadFinderSubscriptionRequest.status = RequestStatus.Failed;
        state.resumeLeadFinderSubscriptionRequest.message =
          action.payload.message;
        state.resumeLeadFinderSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Resume Inbox Analyzer subscription request
    builder.addCase(resumeInboxAnalyzerSubscriptionRequest.pending, (state) => {
      state.resumeInboxAnalyzerSubscriptionRequest.status =
        RequestStatus.Pending;
      state.resumeInboxAnalyzerSubscriptionRequest.message = null;
      state.resumeInboxAnalyzerSubscriptionRequest.error = null;
    });
    builder.addCase(
      resumeInboxAnalyzerSubscriptionRequest.fulfilled,
      (state, action) => {
        state.resumeInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.resumeInboxAnalyzerSubscriptionResponse = action.payload?.payload;
        state.resumeInboxAnalyzerSubscriptionRequest.message = null;
        state.resumeInboxAnalyzerSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      resumeInboxAnalyzerSubscriptionRequest.rejected,
      (state, action) => {
        state.resumeInboxAnalyzerSubscriptionRequest.status =
          RequestStatus.Failed;
        state.resumeInboxAnalyzerSubscriptionRequest.message =
          action.payload.message;
        state.resumeInboxAnalyzerSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Handle Subscription Request
    builder.addCase(handleSubscriptionRequest.pending, (state) => {
      state.handleSubscriptionRequest.status = RequestStatus.Pending;
      state.handleSubscriptionRequest.message = null;
      state.handleSubscriptionRequest.error = null;
    });
    builder.addCase(handleSubscriptionRequest.fulfilled, (state, action) => {
      state.handleSubscriptionRequest.status = RequestStatus.Succeeded;
      state.handleSubscriptionResponse = action.payload.payload;
      state.handleSubscriptionRequest.message = null;
      state.handleSubscriptionRequest.error = null;
    });
    builder.addCase(handleSubscriptionRequest.rejected, (state, action) => {
      state.handleSubscriptionRequest.status = RequestStatus.Failed;
      state.handleSubscriptionRequest.message = action.payload.message;
      state.handleSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Handle Lead Finder Subscription Request
    builder.addCase(
      handleLeadFinderChurnkeySubscriptionRequest.pending,
      (state) => {
        state.handleLeadFinderChurnkeySubscriptionRequest.status =
          RequestStatus.Pending;
        state.handleLeadFinderChurnkeySubscriptionRequest.message = null;
        state.handleLeadFinderChurnkeySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      handleLeadFinderChurnkeySubscriptionRequest.fulfilled,
      (state, action) => {
        state.handleLeadFinderChurnkeySubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.handleLeadFinderChurnkeySubscriptionResponse =
          action.payload.payload;
        state.handleLeadFinderChurnkeySubscriptionRequest.message = null;
        state.handleLeadFinderChurnkeySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      handleLeadFinderChurnkeySubscriptionRequest.rejected,
      (state, action) => {
        state.handleLeadFinderChurnkeySubscriptionRequest.status =
          RequestStatus.Failed;
        state.handleLeadFinderChurnkeySubscriptionRequest.message =
          action.payload.message;
        state.handleLeadFinderChurnkeySubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Handle Inbox Analyzer Subscription Request
    builder.addCase(
      handleInboxAnalyzerChurnkeySubscriptionRequest.pending,
      (state) => {
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.status =
          RequestStatus.Pending;
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.message = null;
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      handleInboxAnalyzerChurnkeySubscriptionRequest.fulfilled,
      (state, action) => {
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.handleInboxAnalyzerChurnkeySubscriptionResponse =
          action.payload.payload;
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.message = null;
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      handleInboxAnalyzerChurnkeySubscriptionRequest.rejected,
      (state, action) => {
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.status =
          RequestStatus.Failed;
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.message =
          action.payload.message;
        state.handleInboxAnalyzerChurnkeySubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Calculate Pay Request
    builder.addCase(calculatePayRequest.pending, (state) => {
      state.calculatePayRequest.status = RequestStatus.Pending;
      state.calculatePayRequest.message = null;
      state.calculatePayRequest.error = null;
    });
    builder.addCase(calculatePayRequest.fulfilled, (state, action) => {
      state.calculatePayRequest.status = RequestStatus.Succeeded;
      state.getCalculatePayResponse = action.payload.payload;
      state.calculatePayRequest.message = action.payload.message;
      state.calculatePayRequest.error = null;
    });
    builder.addCase(calculatePayRequest.rejected, (state, action) => {
      state.calculatePayRequest.status = RequestStatus.Failed;
      state.calculatePayRequest.message = action.payload.message;
      state.calculatePayRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Calculate Inbox Analyzer Pay Request
    builder.addCase(calculateInboxAnalyzerPayRequest.pending, (state) => {
      state.calculateInboxAnalyzerPayRequest.status = RequestStatus.Pending;
      state.calculateInboxAnalyzerPayRequest.message = null;
      state.calculateInboxAnalyzerPayRequest.error = null;
    });
    builder.addCase(
      calculateInboxAnalyzerPayRequest.fulfilled,
      (state, action) => {
        state.calculateInboxAnalyzerPayRequest.status = RequestStatus.Succeeded;
        state.getCalculateInboxAnalyzerPayResponse = action.payload.payload;
        state.calculateInboxAnalyzerPayRequest.message = action.payload.message;
        state.calculateInboxAnalyzerPayRequest.error = null;
      },
    );
    builder.addCase(
      calculateInboxAnalyzerPayRequest.rejected,
      (state, action) => {
        state.calculateInboxAnalyzerPayRequest.status = RequestStatus.Failed;
        state.calculateInboxAnalyzerPayRequest.message = action.payload.message;
        state.calculateInboxAnalyzerPayRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Upgrade Plan Request
    builder.addCase(upgradePlanRequest.pending, (state) => {
      state.upgradePlanRequest.status = RequestStatus.Pending;
      state.upgradePlanRequest.message = null;
      state.upgradePlanRequest.error = null;
    });
    builder.addCase(upgradePlanRequest.fulfilled, (state) => {
      state.upgradePlanRequest.status = RequestStatus.Succeeded;
      state.upgradePlanRequest.message = null;
      state.upgradePlanRequest.error = null;
    });
    builder.addCase(upgradePlanRequest.rejected, (state, action) => {
      state.upgradePlanRequest.status = RequestStatus.Failed;
      state.upgradePlanRequest.message = action.payload.message;
      state.upgradePlanRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Downgrade Plan Request
    builder.addCase(downgradePlanRequest.pending, (state) => {
      state.downgradePlanRequest.status = RequestStatus.Pending;
      state.downgradePlanRequest.message = null;
      state.downgradePlanRequest.error = null;
    });
    builder.addCase(downgradePlanRequest.fulfilled, (state) => {
      state.downgradePlanRequest.status = RequestStatus.Succeeded;
      state.downgradePlanRequest.message = null;
      state.downgradePlanRequest.error = null;
    });
    builder.addCase(downgradePlanRequest.rejected, (state, action) => {
      state.downgradePlanRequest.status = RequestStatus.Failed;
      state.downgradePlanRequest.message = action.payload.message;
      state.downgradePlanRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // LTD Purchase Plan Request
    builder.addCase(purchaseLtdPlanRequest.pending, (state) => {
      state.purchaseLtdPlanRequest.status = RequestStatus.Pending;
      state.purchaseLtdPlanRequest.message = null;
      state.purchaseLtdPlanRequest.error = null;
    });
    builder.addCase(purchaseLtdPlanRequest.fulfilled, (state, action) => {
      state.purchaseLtdPlanRequest.status = RequestStatus.Succeeded;
      state.purchaseLtdPlanRequest.message = action.payload.message;
      state.purchaseLtdPlanRequest.error = null;
      state.purchaseLtdPlanResponse = action.payload.payload;
    });
    builder.addCase(purchaseLtdPlanRequest.rejected, (state, action) => {
      state.purchaseLtdPlanRequest.status = RequestStatus.Failed;
      state.purchaseLtdPlanRequest.message = action.payload.message;
      state.purchaseLtdPlanRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // LTD Upgrade Plan Request
    builder.addCase(upgradeLtdPlanRequest.pending, (state) => {
      state.upgradeLtdPlanRequest.status = RequestStatus.Pending;
      state.upgradeLtdPlanRequest.message = null;
      state.upgradeLtdPlanRequest.error = null;
    });
    builder.addCase(upgradeLtdPlanRequest.fulfilled, (state, action) => {
      state.upgradeLtdPlanRequest.status = RequestStatus.Succeeded;
      state.upgradeLtdPlanRequest.message = action.payload.message;
      state.upgradeLtdPlanRequest.error = null;
      state.upgradeLtdPlanResponse = action.payload.payload;
    });
    builder.addCase(upgradeLtdPlanRequest.rejected, (state, action) => {
      state.upgradeLtdPlanRequest.status = RequestStatus.Failed;
      state.upgradeLtdPlanRequest.message = action.payload.message;
      state.upgradeLtdPlanRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // LTD Modify Plan Request
    builder.addCase(modifyLtdPlanRequest.pending, (state) => {
      state.modifyLtdPlanRequest.status = RequestStatus.Pending;
      state.modifyLtdPlanRequest.message = null;
      state.modifyLtdPlanRequest.error = null;
    });
    builder.addCase(modifyLtdPlanRequest.fulfilled, (state, action) => {
      state.modifyLtdPlanRequest.status = RequestStatus.Succeeded;
      state.modifyLtdPlanRequest.message = action.payload.message;
      state.modifyLtdPlanRequest.error = null;
      state.modifyLtdPlanResponse = action.payload.payload;
    });
    builder.addCase(modifyLtdPlanRequest.rejected, (state, action) => {
      state.modifyLtdPlanRequest.status = RequestStatus.Failed;
      state.modifyLtdPlanRequest.message = action.payload.message;
      state.modifyLtdPlanRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Purchase History
    builder.addCase(getPurchaseHistoryRequest.pending, (state) => {
      state.getPurchaseHistoryRequest.status = RequestStatus.Pending;
      state.getPurchaseHistoryRequest.error = null;
      state.getPurchaseHistoryRequest.message = null;
    });
    builder.addCase(getPurchaseHistoryRequest.fulfilled, (state, action) => {
      state.getPurchaseHistoryRequest.status = RequestStatus.Succeeded;
      state.purchaseHistory = action.payload.payload.history;
    });
    builder.addCase(getPurchaseHistoryRequest.rejected, (state, action) => {
      state.getPurchaseHistoryRequest.status = RequestStatus.Failed;
      state.getPurchaseHistoryRequest.message = action.payload.message;
      state.getPurchaseHistoryRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Purchase Details By Id
    builder.addCase(getPurchaseDetailsByIdRequest.pending, (state) => {
      state.getPurchaseDetailsByIdRequest.status = RequestStatus.Pending;
      state.getPurchaseDetailsByIdRequest.error = null;
      state.getPurchaseDetailsByIdRequest.message = null;
    });
    builder.addCase(
      getPurchaseDetailsByIdRequest.fulfilled,
      (state, action) => {
        state.getPurchaseDetailsByIdRequest.status = RequestStatus.Succeeded;
        state.purchaseDetailsById = action.payload.payload;
      },
    );
    builder.addCase(getPurchaseDetailsByIdRequest.rejected, (state, action) => {
      state.getPurchaseDetailsByIdRequest.status = RequestStatus.Failed;
      state.getPurchaseDetailsByIdRequest.message = action.payload.message;
      state.getPurchaseDetailsByIdRequest.error =
        !action.payload.isHandled && action.payload;
    });

    builder.addCase(renewDomainsRequest.pending, (state) => {
      state.renewDomainsRequest.status = RequestStatus.Pending;
      state.renewDomainsRequest.error = null;
      state.renewDomainsRequest.message = null;
      state.renewDomainsResponse = null;
    });
    builder.addCase(renewDomainsRequest.fulfilled, (state, action) => {
      state.renewDomainsRequest.status = RequestStatus.Succeeded;
      state.renewDomainsResponse = action.payload ?? null;
    });
    builder.addCase(renewDomainsRequest.rejected, (state, action) => {
      state.renewDomainsRequest.status = RequestStatus.Failed;
      state.renewDomainsRequest.message = action.payload?.message;
      state.renewDomainsRequest.error =
        !action.payload?.isHandled && action.payload;
      state.renewDomainsResponse = null;
    });

    builder.addCase(exportCreditHistoryRequest.pending, (state) => {
      state.exportCreditHistoryRequest.status = RequestStatus.Pending;
      state.exportCreditHistoryRequest.error = null;
      state.exportCreditHistoryRequest.message = null;
    });

    builder.addCase(exportCreditHistoryRequest.fulfilled, (state, action) => {
      state.exportCreditHistoryRequest.status = RequestStatus.Succeeded;
      state.exportCreditHistoryRequest.message = action.payload.payload.message;
    });

    builder.addCase(exportCreditHistoryRequest.rejected, (state, action) => {
      state.exportCreditHistoryRequest.status = RequestStatus.Failed;
      state.exportCreditHistoryRequest.message = action.payload.message;
      state.exportCreditHistoryRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Lead Finder Plan Request
    builder.addCase(getLeadFinderOneTimePlansRequest.pending, (state) => {
      state.getLeadFinderOneTimePlansRequest.status = RequestStatus.Pending;
      state.getLeadFinderOneTimePlansRequest.message = null;
      state.getLeadFinderOneTimePlansRequest.error = null;
    });

    builder.addCase(
      getLeadFinderOneTimePlansRequest.fulfilled,
      (state, action) => {
        state.getLeadFinderOneTimePlansRequest.status = RequestStatus.Succeeded;
        state.getLeadFinderOneTimePlansResponse =
          action.payload.payload?.rows || action.payload.payload || [];
        state.getLeadFinderOneTimePlansRequest.message =
          action.payload.payload.message;
      },
    );

    builder.addCase(
      getLeadFinderOneTimePlansRequest.rejected,
      (state, action) => {
        state.getLeadFinderOneTimePlansRequest.status = RequestStatus.Failed;
        state.getLeadFinderOneTimePlansRequest.message = action.payload.message;
        state.getLeadFinderOneTimePlansRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Purchase Lead Finder Plan Request
    builder.addCase(purchaseLeadFinderOneTimePlanRequest.pending, (state) => {
      state.purchaseLeadFinderOneTimePlanRequest.status = RequestStatus.Pending;
      state.purchaseLeadFinderOneTimePlanRequest.message = null;
      state.purchaseLeadFinderOneTimePlanRequest.error = null;
    });

    builder.addCase(
      purchaseLeadFinderOneTimePlanRequest.fulfilled,
      (state, action) => {
        state.purchaseLeadFinderOneTimePlanRequest.status =
          RequestStatus.Succeeded;
        state.purchaseLeadFinderOneTimePlanResponse = action.payload.payload;
        state.purchaseLeadFinderOneTimePlanRequest.message =
          action.payload.payload.message;
        state.purchaseLeadFinderOneTimePlanRequest.error = null;
      },
    );

    builder.addCase(
      purchaseLeadFinderOneTimePlanRequest.rejected,
      (state, action) => {
        state.purchaseLeadFinderOneTimePlanRequest.status =
          RequestStatus.Failed;
        state.purchaseLeadFinderOneTimePlanRequest.message =
          action.payload.message;
        state.purchaseLeadFinderOneTimePlanRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Calculate Ai Credits Pricings Request
    builder.addCase(calculateAiCreditsPricingsRequest.pending, (state) => {
      state.calculateAiCreditsPricingsRequest.status = RequestStatus.Pending;
      state.calculateAiCreditsPricingsRequest.message = null;
      state.calculateAiCreditsPricingsRequest.error = null;
    });
    builder.addCase(
      calculateAiCreditsPricingsRequest.fulfilled,
      (state, action) => {
        state.calculateAiCreditsPricingsRequest.status =
          RequestStatus.Succeeded;
        state.calculateAiCreditsPricingsRequest.message =
          action.payload.payload.message;
        state.calculateAiCreditsPricingsRequest.error = null;
        state.calculateAiCreditsPricingsResponse = action.payload?.payload;
      },
    );
    builder.addCase(
      calculateAiCreditsPricingsRequest.rejected,
      (state, action) => {
        state.calculateAiCreditsPricingsRequest.status = RequestStatus.Failed;
        state.calculateAiCreditsPricingsRequest.message =
          action.payload.message;
        state.calculateAiCreditsPricingsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Purchase Ai Credits Request
    builder.addCase(purchaseAiCreditsRequest.pending, (state) => {
      state.purchaseAiCreditsRequest.status = RequestStatus.Pending;
      state.purchaseAiCreditsRequest.message = null;
      state.purchaseAiCreditsRequest.error = null;
    });

    builder.addCase(purchaseAiCreditsRequest.fulfilled, (state, action) => {
      state.purchaseAiCreditsRequest.status = RequestStatus.Succeeded;
      state.purchaseAiCreditsRequest.message = null;
      state.purchaseAiCreditsResponse = action.payload.payload;
      state.purchaseAiCreditsRequest.error = null;
    });

    builder.addCase(purchaseAiCreditsRequest.rejected, (state, action) => {
      state.purchaseAiCreditsRequest.status = RequestStatus.Failed;
      state.purchaseAiCreditsRequest.message = action.payload.message;
      state.purchaseAiCreditsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Default Credit Rate Request
    builder.addCase(getDefaultAiCreditRateRequest.pending, (state) => {
      state.getDefaultAiCreditRateRequest.status = RequestStatus.Pending;
      state.getDefaultAiCreditRateRequest.message = null;
      state.getDefaultAiCreditRateRequest.error = null;
    });

    builder.addCase(
      getDefaultAiCreditRateRequest.fulfilled,
      (state, action) => {
        state.getDefaultAiCreditRateRequest.status = RequestStatus.Succeeded;
        state.getDefaultAiCreditRateRequest.message =
          action.payload.payload.message;
        state.getDefaultAiCreditRateRequest.error = null;
        state.defaultAiCreditRateResponse = action.payload.payload;
      },
    );

    builder.addCase(getDefaultAiCreditRateRequest.rejected, (state, action) => {
      state.getDefaultAiCreditRateRequest.status = RequestStatus.Failed;
      state.getDefaultAiCreditRateRequest.message = action.payload.message;
      state.getDefaultAiCreditRateRequest.error =
        !action.payload.isHandled && action.payload;
    });

    builder.addCase(aiCreditHistoryRequest.pending, (state) => {
      state.aiCreditHistoryRequest.status = RequestStatus.Pending;
      state.aiCreditHistoryRequest.error = null;
      state.aiCreditHistoryRequest.message = null;
    });

    builder.addCase(aiCreditHistoryRequest.fulfilled, (state, action) => {
      state.aiCreditHistoryRequest.status = RequestStatus.Succeeded;
      state.aiCreditHistoryRequest.message = action.payload.payload.message;
      state.aiCreditHistoryRequest.error = null;
    });

    builder.addCase(aiCreditHistoryRequest.rejected, (state, action) => {
      state.aiCreditHistoryRequest.status = RequestStatus.Failed;
      state.aiCreditHistoryRequest.message = action.payload.message;
      state.aiCreditHistoryRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Scheduled Upgrade Plan Request
    builder.addCase(schedulePlanUpgradeRequest.pending, (state) => {
      state.schedulePlanUpgradeRequest.status = RequestStatus.Pending;
      state.schedulePlanUpgradeRequest.message = null;
      state.schedulePlanUpgradeRequest.error = null;
    });

    builder.addCase(schedulePlanUpgradeRequest.fulfilled, (state, action) => {
      state.schedulePlanUpgradeRequest.status = RequestStatus.Succeeded;
      state.schedulePlanUpgradeRequest.message = action.payload.payload.message;
      state.schedulePlanUpgradeRequest.error = null;
    });

    builder.addCase(schedulePlanUpgradeRequest.rejected, (state, action) => {
      state.schedulePlanUpgradeRequest.status = RequestStatus.Failed;
      state.schedulePlanUpgradeRequest.message = action.payload.message;
      state.schedulePlanUpgradeRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Calculate Credits Prices Request
    builder.addCase(calculateCreditsPricesRequest.pending, (state) => {
      state.calculateCreditsPricesRequest.status = RequestStatus.Pending;
      state.calculateCreditsPricesRequest.message = null;
      state.calculateCreditsPricesRequest.error = null;
    });

    builder.addCase(
      calculateCreditsPricesRequest.fulfilled,
      (state, action) => {
        state.calculateCreditsPricesRequest.status = RequestStatus.Succeeded;
        state.calculateCreditsPricesRequest.message =
          action.payload?.payload?.message;
        state.calculateCreditsPricesRequest.error = null;
        state.calculateCreditsPricesResponse = action.payload?.payload;
      },
    );

    builder.addCase(calculateCreditsPricesRequest.rejected, (state, action) => {
      state.calculateCreditsPricesRequest.status = RequestStatus.Failed;
      state.calculateCreditsPricesRequest.message = action.payload?.message;
      state.calculateCreditsPricesRequest.error =
        !action.payload?.isHandled && action.payload;
    });

    // Purchase Credits Request
    builder.addCase(purchaseCreditsRequest.pending, (state) => {
      state.purchaseCreditsRequest.status = RequestStatus.Pending;
      state.purchaseCreditsRequest.message = null;
      state.purchaseCreditsRequest.error = null;
    });

    builder.addCase(purchaseCreditsRequest.fulfilled, (state, action) => {
      state.purchaseCreditsRequest.status = RequestStatus.Succeeded;
      state.purchaseCreditsRequest.message = action.payload?.payload?.message;
      state.purchaseCreditsRequest.error = null;
      state.purchaseCreditsResponse = action.payload.payload;
    });

    builder.addCase(purchaseCreditsRequest.rejected, (state, action) => {
      state.purchaseCreditsRequest.status = RequestStatus.Failed;
      state.purchaseCreditsRequest.message = action.payload?.message;
      state.purchaseCreditsRequest.error =
        !action.payload?.isHandled && action.payload;
    });

    // Get Credits Transaction Status Request
    builder.addCase(getCreditsTransactionStatusRequest.pending, (state) => {
      state.getCreditsTransactionStatusRequest.status = RequestStatus.Pending;
      state.getCreditsTransactionStatusRequest.message = null;
      state.getCreditsTransactionStatusRequest.error = null;
    });

    builder.addCase(
      getCreditsTransactionStatusRequest.fulfilled,
      (state, action) => {
        state.getCreditsTransactionStatusRequest.status =
          RequestStatus.Succeeded;
        state.getCreditsTransactionStatusRequest.message =
          action.payload?.payload?.message;
        state.getCreditsTransactionStatusRequest.error = null;
        state.getCreditsTransactionStatusResponse = action.payload?.payload;
      },
    );

    builder.addCase(
      getCreditsTransactionStatusRequest.rejected,
      (state, action) => {
        state.getCreditsTransactionStatusRequest.status = RequestStatus.Failed;
        state.getCreditsTransactionStatusRequest.message =
          action.payload?.message;
        state.getCreditsTransactionStatusRequest.error =
          !action.payload?.isHandled && action.payload;
      },
    );

    // ─────────────────────────────────────────────────────────────────────────
    // PADDLE-ONLY EXTRA REDUCERS
    // ─────────────────────────────────────────────────────────────────────────

    // Get Paddle Subscription Metadata
    builder.addCase(getPaddleSubscriptionMetadataRequest.pending, (state) => {
      state.getPaddleSubscriptionMetadataStatus = RequestStatus.Pending;
    });
    builder.addCase(getPaddleSubscriptionMetadataRequest.fulfilled, (state, action) => {
      state.getPaddleSubscriptionMetadataStatus = RequestStatus.Succeeded;
      state.paddleSubscriptionMetadata = action.payload as any;
    });
    builder.addCase(getPaddleSubscriptionMetadataRequest.rejected, (state) => {
      state.getPaddleSubscriptionMetadataStatus = RequestStatus.Failed;
    });

    // Poll Paddle Transaction Status
    builder.addCase(pollPaddleTransactionStatusRequest.pending, (state) => {
      state.pollPaddleTransactionStatusStatus = RequestStatus.Pending;
    });
    builder.addCase(pollPaddleTransactionStatusRequest.fulfilled, (state, action) => {
      state.pollPaddleTransactionStatusStatus = RequestStatus.Succeeded;
    });
    builder.addCase(pollPaddleTransactionStatusRequest.rejected, (state) => {
      state.pollPaddleTransactionStatusStatus = RequestStatus.Failed;
    });

    // Create Paddle Customer Portal Session
    builder.addCase(createPaddleCustomerPortalSessionRequest.pending, (state) => {
      state.createPaddleCustomerPortalSessionStatus = RequestStatus.Pending;
    });
    builder.addCase(createPaddleCustomerPortalSessionRequest.fulfilled, (state, action) => {
      state.createPaddleCustomerPortalSessionStatus = RequestStatus.Succeeded;
    });
    builder.addCase(createPaddleCustomerPortalSessionRequest.rejected, (state) => {
      state.createPaddleCustomerPortalSessionStatus = RequestStatus.Failed;
    });

    // Revoke Paddle Scheduled Pause
    builder.addCase(revokePaddleScheduledPauseRequest.pending, (state) => {
      state.revokePaddleScheduledPauseStatus = RequestStatus.Pending;
    });
    builder.addCase(revokePaddleScheduledPauseRequest.fulfilled, (state, action) => {
      state.revokePaddleScheduledPauseStatus = RequestStatus.Succeeded;
    });
    builder.addCase(revokePaddleScheduledPauseRequest.rejected, (state) => {
      state.revokePaddleScheduledPauseStatus = RequestStatus.Failed;
    });

    // Revoke Pause Subscription
    builder.addCase(revokePauseSubscriptionRequest.pending, (state) => {
      state.revokePauseSubscriptionRequest.status = RequestStatus.Pending;
      state.revokePauseSubscriptionRequest.message = null;
      state.revokePauseSubscriptionRequest.error = null;
    });
    builder.addCase(revokePauseSubscriptionRequest.fulfilled, (state) => {
      state.revokePauseSubscriptionRequest.status = RequestStatus.Succeeded;
      state.revokePauseSubscriptionRequest.message = null;
      state.revokePauseSubscriptionRequest.error = null;
    });
    builder.addCase(revokePauseSubscriptionRequest.rejected, (state, action) => {
      state.revokePauseSubscriptionRequest.status = RequestStatus.Failed;
      state.revokePauseSubscriptionRequest.message = action.payload.message;
      state.revokePauseSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });
  },
});

export const {
  resetSubscription,
  deleteAppliedCouponCode,
  resetPurchaseEmailVerificationCreditsResponse,
  resetPurchaseModifySubscriptionResponse,
  resetPurchaseLeadFinderModifySubscriptionResponse,
  resetCancelLeadFinderAddonRequest,
  resetModifySubscriptionResponse,
  resetInboxAnalyzerModifySubscriptionResponse,
  resetCalculatePayState,
  resetCalculateInboxAnalyzerPayState,
  resetReactivateSubscriptionResponse,
  resetReactivateLeadFinderSubscriptionResponse,
  resetReactivateInboxAnalyzerSubscriptionResponse,
  resetResumeSubscriptionResponse,
  resetResumeLeadFinderSubscriptionResponse,
  resetResumeInboxAnalyzerSubscriptionResponse,
  resetRevokePauseSubscriptionRequest,
  resetConnectedUsersAndEmailAccountsRequest,
  resetHandleSubscriptionResponse,
  resetHandleLeadFinderSubscriptionChurnkeyResponse,
  resetHandleInboxAnalyzerSubscriptionChurnkeyResponse,
  resetPurchaseLtdPlan,
  resetUpgradeLtdPlan,
  resetModifyLtdPlan,
  selectPlan,
  resetInvoice,
  resetRenewDomainsResponse,
  resetInboxAnalyzerInvoice,
  resetPurchaseDetails,
  resetExportCreditHistoryRequest,
  resetAiCreditHistoryRequest,
  resetPurchaseLeadFinderOneTimePlan,
  resetSchedulePlanUpgradeRequest,
  resetCalculateCreditsPricesRequest,
  resetPurchaseCreditsRequest,
  resetGetCreditsTransactionStatusRequest,
  resetPurchaseSubscriptionRequestStatus,
  resetPurchaseLeadFinderSubscriptionRequestStatus,
  resetPurchaseInboxAnalyzerSubscriptionRequestStatus,
  resetPurchaseInboxAnalyzerModifySubscriptionResponse,
  startPaddlePolling,
  stopPaddlePolling,
  incrementPaddlePollingCount,
  setPaddlePollingStatus,
} = billingSubscriptionSlice.actions;

export default billingSubscriptionSlice.reducer;
